/**!
 * Created by Leonardo Cheslacki on 22/06/2024.
 *
 * MIT License
 *
 * Copyright (c) 2024 cheslacki
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';

import StageController from './StageController';

import {HelpersIcon, HelpersPin} from './../helpers';

import Big from 'big.js';

import config from '../../../../config';

const {default: {orientation: {speed, hours, stations, degrees, slide}}} = config;

import WithType from './../helpers/WithType';

let _this = {};

/**
 *
 */
class StageSun extends StageController {

    /**
     *
     * @param props
     * @param main
     */
    constructor(props, main = {}) {
        super(props);
        this.props = props;
        this.main = main;

        const {current_name} = this.props;

        this.state = {};

        this.$big_frame = this.$controller.big_1;

        this.$referenceSun = null;

        this.$thisFrame = null;

        this.$helpersIcon = HelpersIcon(this)({current_name: current_name});
        this.$helpersPin = HelpersPin(this)({current_name: current_name});

        this.$supportTouchCanvas = null;

        _this = this;
    };

    $functions = {

        /**
         * Stop Sun
         */
        handleSlideMouseDown: function () {
            _this.destroyTicker('_handleSlideSyncSun');
        },

        /**
         * Move slide and sun.
         * @param big_positionX
         */
        handleSlidePressMove: function ({big_positionX}) {
            _this.setPosition(big_positionX);
        },

        /**
         * Stop Sun and position slide.
         * @param big_clientX
         * @param big_clientY
         */
        handleSlideTouchStart: function ({big_clientX, big_clientY}) {
            const {occ_slider} = _this.$thisFrame;

            _this.setPosition(big_clientX.minus(new Big(occ_slider.x)), _this.$supportTouchCanvas.checkLimitation(occ_slider, big_clientX, big_clientY, occ_slider.x, occ_slider.y));

            _this.destroyTicker('_handleSlideSyncSun');
        },

        /**
         * Move slide and sun.
         * @param event
         * @param big_clientX
         * @param big_clientY
         */
        handleSlideTouchMove: function (event, {big_clientX, big_clientY}) {
            _this.stopPropagation(event);

            const {occ_slider} = _this.$thisFrame;

            //_this.testCoordinate(stage, big_clientX, big_clientY);

            _this.setPosition(big_clientX.minus(new Big(occ_slider.x)), _this.$supportTouchCanvas.checkLimitation(occ_slider, big_clientX, big_clientY, occ_slider.x, occ_slider.y));
        },

        /**
         *
         * @param event
         * @param data
         */
        handleSlideHours: function (event, {data}) {
            const
                {occ_plant: {occ_effect: {totalFrames}}} = _this.$thisFrame,
                {big_slider_width, big_slide_width, big_limit, big_position, big_speed} = data;

            _this.$big_frame = _this.$big_frame.plus(big_speed);

            _this.$referenceSun.setCoordinateSun(_this.calcSize(big_slider_width.minus(big_slide_width), _this.$big_frame.minus(big_limit), totalFrames, false, 'int'));
            _this.$referenceSun.setCoordinateSlide(_this.bigToNumber(_this.$big_frame, false, 'int'));

            _this.$big_frame.gte(big_position) && _this.destroyTicker('_handleSlideSyncSun');
        },

        /**
         *
         * @param event
         * @param occ
         */
        handleSlideStationMouseDown: function (event, {occ}) {
            _this.changeStation(occ);
        }
    };

    /**
     *
     * @param big_positionX
     * @param enabled
     */
    setPosition = (big_positionX, enabled = true) => {
        const {occ_slider, occ_plant: {occ_effect: {totalFrames}}, occ_slider: {occ_slide}} = this.$thisFrame;

        let
            big_slider_width = (new Big(occ_slider.nominalBounds.width)),
            big_slide_width = (new Big(occ_slide.nominalBounds.width)),
            big_limit = big_slide_width.div(this.$controller.big_2);

        /**
         * Limitation slide.
         */
        if (big_positionX.gte(big_limit) && big_positionX.lte(big_slider_width.minus(big_limit)) && enabled) {
            this.$referenceSun.setCoordinateSun(this.calcSize(big_slider_width.minus(big_slide_width), big_positionX.minus(big_limit), totalFrames, false, 'int'));
            this.$referenceSun.setCoordinateSlide(this.bigToNumber(big_positionX, false, 'int'));
        }
    };

    setColors = () => {
        const
            {
                occ_slider_title,
                occ_slider,
                occ_slider_arrow
            } = this.$thisFrame;

        occ_slider_title.occ_left.color = this.tintToHex(slide.color_title);
        occ_slider_title.occ_center.color = this.tintToHex(slide.color_title);
        occ_slider_title.occ_right.color = this.tintToHex(slide.color_title);

        for (let property of occ_slider.occ_rule.occ_line.children) {
            this.$helpersPin.setColor(property, slide.color_line);
        }

        for (let property of occ_slider.occ_rule.occ_ruler.children) {
            this.$helpersPin.setColor(property, slide.color_line);
        }

        this.$helpersPin.setColor(occ_slider_arrow, slide.color_arrow);

        this.$helpersPin.setColor(occ_slider.occ_slide.occ_round, slide.color_round);
        this.$helpersPin.setColor(occ_slider.occ_slide.occ_color, slide.color_circle);
    };

    /**
     *
     * @param occ
     */
    pseudoAfter = (occ) => {
        const
            {
                btn_autumn,
                btn_winter,
                btn_spring,
                btn_summer,
                pseudo_autumn,
                pseudo_winter,
                pseudo_spring,
                pseudo_summer
            } = this.$thisFrame;

        let
            autumn = (occ === 'autumn'),
            winter = (occ === 'winter'),
            spring = (occ === 'spring'),
            summer = (occ === 'summer');

        btn_autumn.visible = !autumn;
        pseudo_autumn.visible = autumn;

        btn_winter.visible = !winter;
        pseudo_winter.visible = winter;

        btn_spring.visible = !spring;
        pseudo_spring.visible = spring;

        btn_summer.visible = !summer;
        pseudo_summer.visible = summer;
    };

    /**
     *
     * @param occ
     */
    changeStation = (occ) => {
        const disabledAnimate = this.$helpersIcon.disabledAnimate(this.$thisFrame);
        this.$referenceSun.setRotation(degrees[occ]);
        disabledAnimate(occ);
        this.pseudoAfter(occ);
    };

    referenceSun = (_this) => {
        return {
            object: null,

            /**
             *
             * @returns {null}
             */
            get sun() {
                return this.object;
            },

            /**
             *
             * @param object
             */
            set sun(object) {
                this.object = object;
            },

            reference() {
                const {occ_plant: {occ_effect, occ_effect: {occ_guide}}, occ_slider} = _this.$thisFrame;
                this.sun = {content: occ_effect, guide: occ_guide, input: occ_slider};
            },

            /**
             *
             * @param x
             */
            setCoordinateSun(x) {
                const {content, guide} = this.object;

                x = _this.toIntegerOrFloat(x);

                if (_this.isIntegerOrFloat(x)) {
                    let position = _this.toInteger(x);
                    content.gotoAndStop(position);
                    guide.gotoAndStop(position);
                }
            },
            /**
             *
             * @param x
             */
            setCoordinateSlide(x) {
                const {input: {occ_slide}} = this.object;

                x = _this.toIntegerOrFloat(x);

                _this.isIntegerOrFloat(x) && occ_slide.setTransform(x, occ_slide.y);
            },
            /**
             *
             * @param degrees
             */
            setRotation(degrees) {
                const {guide} = this.object;

                guide.setTransform(guide.x, guide.y, guide.scaleX, guide.scaleY, degrees);
            }
        }
    };

    syncHours = () => {
        const {occ_slider, occ_slider: {occ_slide}} = this.$thisFrame;

        let
            range = this.createArray(hours.start, hours.end, 1),
            index = range.findIndex((value) => (value === this.$hours)),
            big_index = (index > -1) ? (new Big(index)) : (new Big(this.$controller.big_0)),
            big_length = (new Big(range.length)),
            big_slider_width = (new Big(occ_slider.nominalBounds.width)),
            big_slide_width = (new Big(occ_slide.nominalBounds.width)),
            big_limit = big_slide_width.div(_this.$controller.big_2),
            big_max = big_slider_width.minus(big_limit),
            big_speed = (new Big(speed));

        let big_position = big_slider_width.minus(big_limit).div(big_length).times(big_index.plus(this.$controller.big_1));

        if (this.$hours <= hours.start) {
            big_position = big_limit;
        } else if (this.$hours >= hours.end) {
            big_position = big_max;
        }

        if (big_position.gte(big_limit) && big_position.lte(big_max)) {
            this.$big_frame = _this.$controller.big_0;

            createjs.Ticker.timingMode = createjs.Ticker.RAF;

            let ticker = createjs.Ticker.on('tick', this.$functions.handleSlideHours, false, false, {
                data: {
                    big_slider_width: big_slider_width,
                    big_slide_width: big_slide_width,
                    big_limit: big_limit,
                    big_position: big_position,
                    big_speed: big_speed
                }
            });

            this.setReferenceTicker('_handleSlideSyncSun', ticker);
        }
    };

    syncStation = () => {
        let
            day = (new Big(this.$day)),
            month = stations.find((object) => (object.month === this.$month));

        /**
         *
         * @param array
         * @returns {boolean}
         */
        const checkStation = (array) => {
            if (this.inArray(array, month.month)) {
                if (this.isEmpty(month.day)) {
                    return true;
                } else if (month.month === this.firstArray(array) && day.gt(new Big(month.day))) {
                    return true;
                } else if (month.month === this.lastArray(array) && day.lte(new Big(month.day))) {
                    return true;
                }
            }
            return false;
        };

        switch (true) {
            case (checkStation([3, 4, 5, 6])):
                this.changeStation('autumn');
                break;
            case (checkStation([6, 7, 8, 9])):
                this.changeStation('winter');
                break;
            case (checkStation([9, 10, 11, 12])):
                this.changeStation('spring');
                break;
            case (checkStation([12, 1, 2, 3])):
                this.changeStation('summer');
                break;
            default:
                break;
        }
    };

    startSun = () => {
        let composition = this.getComposition(this.props.current_name);
        this.isEmpty(composition) && (composition = this.getComposition(this.props.current_page));

        const
            {properties} = composition.getLibrary(),
            {btn_autumn, btn_winter, btn_spring, btn_summer, occ_slider: {occ_slide}} = this.$thisFrame;

        const handle = (event, {type, state}) => {
            switch (type) {
                case '_MOUSE_DOWN_':
                    this.$functions.handleSlideMouseDown();
                    break;
                case '_MOUSE_MOVE_':
                    this.$functions.handleSlidePressMove(state);
                    break;
                case '_TOUCH_START_':
                    this.$functions.handleSlideTouchStart(state);
                    break;
                case '_TOUCH_MOVE_':
                    this.$functions.handleSlideTouchMove(event, state);
                    break;
                default:
            }
        };

        this.$supportTouchCanvas = this.main.$supportTouchCanvas({
            occ: occ_slide,
            properties: properties,
            callback: handle
        });

        this.$supportTouchCanvas.initTouch();

        this.setHandleMouseDown(btn_autumn, this.$functions.handleSlideStationMouseDown, {occ: 'autumn'});

        this.setHandleMouseDown(btn_winter, this.$functions.handleSlideStationMouseDown, {occ: 'winter'});

        this.setHandleMouseDown(btn_spring, this.$functions.handleSlideStationMouseDown, {occ: 'spring'});

        this.setHandleMouseDown(btn_summer, this.$functions.handleSlideStationMouseDown, {occ: 'summer'});

        this.syncHours();

        this.syncStation();
    };

    initSun = () => {
        this.$thisFrame = this.getFrame();
        this.$referenceSun = this.referenceSun(this);
        this.$referenceSun.reference();
        this.$helpersIcon.setIcon(this.$thisFrame);
        this.setColors();
        this.startSun();
    };
}

/**
 *
 * @type {{}}
 */
StageSun.propTypes = {
    current_name: PropTypes.string,
    current_page: PropTypes.string
};

/**
 *
 * @type {{}}
 */
StageSun.defaultProps = {
    current_name: null,
    current_page: null
};

/**
 *
 * @param ClassObject
 * @param className {className: 'className'}
 * @returns {function(*): function({}=): *} pass _this ClassMain (required) and props {} (optional)
 */
export default WithType(StageSun, {className: 'StageSun'});
